import type {
  FuenteDeAguaPropuesta,
  FuenteDeLaPropuesta,
  ObraDeAguaPropuesta,
} from "@/lib/rellenoConIa";
import { textoDeDecimal } from "@/lib/montos";

import { SIN_IDENTIDAD, type FilaDeFuenteDeAgua, type FilaDeObraDeAgua } from "./aguas";

/* ---------------------------------------------------------------------------
   "Rellenar con IA" en el "Descriptor de aguas": mezclar lo que el modelo
   propuso con lo que ya hay en el formulario.

   VIVE FUERA DEL COMPONENTE porque es lo único de esta entrega que puede estar
   mal sin que nada se vea roto:

   - Un merge que PISE lo que la persona escribió borra su trabajo sin un solo
     error en consola.
   - Un merge que DUPLIQUE al segundo clic duplica el total de agua disponible
     del predio, que es la cifra que quien tasa mira y que la pantalla suma
     sola.

   Dentro del árbol, un test solo podría ejercitarlo a través de `render` y no
   distinguiría una mezcla buena de una que pisa. Es el mismo criterio con el
   que la agrupación de plantaciones vive en `plantaciones.ts` y el total de
   aguas en `aguas.ts`.
--------------------------------------------------------------------------- */

interface Borrador {
  fuentes: FilaDeFuenteDeAgua[];
  obras: FilaDeObraDeAgua[];
}

interface Propuesta {
  fuentes: FuenteDeAguaPropuesta[];
  obras: ObraDeAguaPropuesta[];
}

export interface Mezcla extends Borrador {
  /** Cuántas fuentes se AGREGARON de verdad, ya descontadas las repetidas. */
  fuentesAgregadas: number;
  obrasAgregadas: number;
  /**
   * De qué documento dice la IA que salió cada fila NUEVA, por su `clave`.
   *
   * VA APARTE Y NO EN LA FILA: la fila es lo que se guarda, y la fuente es una
   * ayuda para revisar que no debe viajar ni persistir. Una clave presente con
   * `null` es "no indicada"; una clave ausente es una fila que no es de la IA.
   */
  fuentesPorClave: Record<string, FuenteDeLaPropuesta | null>;
}

/**
 * Agrega lo propuesto a lo que ya hay, sin tocar nada de lo anterior.
 *
 * ══ AGREGA, NUNCA REEMPLAZA ═════════════════════════════════════════════════
 *
 * La propuesta se suma al final y las filas que ya estaban se quedan tal cual.
 * Las dos direcciones no son simétricas: quitar una fila de más es un clic, y
 * recuperar la que el botón se llevó no se puede. Además la pestaña se llena a
 * lo largo de días, a medida que el cliente manda papeles: quien aprieta el
 * botón por segunda vez —ahora que llegó el certificado de la DGA— no está
 * pidiendo empezar de cero.
 *
 * ══ PERO NO REPITE LO QUE YA ESTÁ ═══════════════════════════════════════════
 *
 * El modelo corre a temperatura cero, así que dos clics sobre el mismo
 * expediente devuelven lo mismo. Sin este guardián, el predio pasaría a tener
 * dos veces el mismo canal y el TOTAL DE AGUA DISPONIBLE se duplicaría — una
 * cifra que la pantalla calcula sola y que nada contradice.
 *
 * La comparación es contra lo que YA ESTABA en el formulario, nunca dentro de
 * la propia propuesta: un predio puede tener de verdad dos pozos con el mismo
 * caudal y ningún otro dato que los distinga, y colapsarlos perdería uno.
 */
export function sumarLaPropuesta(actual: Borrador, propuesta: Propuesta): Mezcla {
  const fuentesPrevias = new Set(actual.fuentes.map(huellaDeFuente));
  const obrasPrevias = new Set(actual.obras.map(huellaDeObra));

  const fuentesPorClave: Mezcla["fuentesPorClave"] = {};

  const fuentesNuevas = propuesta.fuentes.flatMap((propuesta) => {
    const fila = filaDesdeLaFuentePropuesta(propuesta);
    if (fuentesPrevias.has(huellaDeFuente(fila))) return [];

    fuentesPorClave[fila.clave] = propuesta.fuente ?? null;
    return [fila];
  });

  const obrasNuevas = propuesta.obras.flatMap((propuesta) => {
    const fila = filaDesdeLaObraPropuesta(propuesta);
    if (obrasPrevias.has(huellaDeObra(fila))) return [];

    fuentesPorClave[fila.clave] = propuesta.fuente ?? null;
    return [fila];
  });

  return {
    fuentes: [...actual.fuentes, ...fuentesNuevas],
    obras: [...actual.obras, ...obrasNuevas],
    fuentesAgregadas: fuentesNuevas.length,
    obrasAgregadas: obrasNuevas.length,
    fuentesPorClave,
  };
}

/**
 * Qué decir después del clic.
 *
 * CUENTA LO QUE SE AGREGÓ, NO LO QUE VINO. Si la propuesta traía dos fuentes
 * que ya estaban, decir "2 fuentes" sería afirmar un cambio en una pantalla
 * donde no cambió nada — y quien lo lea se va a poner a buscarlas.
 */
export function resumenDeLoPropuesto(fuentes: number, obras: number): string {
  if (fuentes === 0 && obras === 0) {
    return "No encontramos nada nuevo que agregar en los documentos leídos.";
  }

  const partes: string[] = [];

  if (fuentes > 0) {
    partes.push(`${fuentes} ${fuentes === 1 ? "fuente de agua" : "fuentes de agua"}`);
  }

  if (obras > 0) {
    partes.push(`${obras} ${obras === 1 ? "obra" : "obras"}`);
  }

  // El verbo concuerda con el TOTAL y no con la primera parte: "Se agregó 1
  // fuente de agua y 1 obra" son dos cosas.
  const verbo = fuentes + obras === 1 ? "Se agregó" : "Se agregaron";

  return `${verbo} ${partes.join(" y ")}.`;
}

/**
 * Una fuente propuesta, con la forma que tiene una fila del formulario.
 *
 * TODO PASA A STRING porque eso es lo que hay en un `<input>`, y los números
 * pasan por `textoDeDecimal`, que pone la COMA decimal. Con el punto, 38.5
 * volvería al servidor como trescientos ochenta y cinco: `numeroEscrito` lee
 * el punto como separador de miles, que es lo que es en este formulario.
 *
 * `validada` SIEMPRE EN FALSO. Esa casilla dice que una persona revisó lo que
 * la IA propuso, así que nacer validada la vaciaría de sentido. No se lee del
 * servidor —la propuesta ni siquiera trae el campo— para que no exista la
 * posibilidad de que un día llegue en `true`.
 */
function filaDesdeLaFuentePropuesta(fuente: FuenteDeAguaPropuesta): FilaDeFuenteDeAgua {
  return {
    clave: crypto.randomUUID(),
    tipo: fuente.tipo,
    rio_canal: fuente.rio_canal ?? "",
    acciones: decimalEscrito(fuente.acciones),
    litros_seg: decimalEscrito(fuente.litros_seg),
    equivalencia_ls_accion: decimalEscrito(fuente.equivalencia_ls_accion),
    estado_inscripcion: fuente.estado_inscripcion ?? "",
    validada: false,
    ...SIN_IDENTIDAD,
  };
}

function filaDesdeLaObraPropuesta(obra: ObraDeAguaPropuesta): FilaDeObraDeAgua {
  return {
    clave: crypto.randomUUID(),
    tipo: obra.tipo,
    capacidad_descripcion: obra.capacidad_descripcion ?? "",
    validada: false,
    ...SIN_IDENTIDAD,
  };
}

/**
 * El número del modelo, escrito como lo escribiría una persona.
 *
 * Reusa `textoDeDecimal` en vez de repetir el reemplazo del punto por la coma:
 * cómo se escribe un decimal en este producto se decide en un solo lugar. Esa
 * función recibe el string de un `decimal:*` de Eloquent, así que el número se
 * convierte antes — es el mismo valor, en el formato que ella espera.
 */
function decimalEscrito(valor: number | null): string {
  return valor === null ? "" : textoDeDecimal(String(valor));
}

/**
 * Qué hace a dos filas "la misma" para no repetirlas.
 *
 * SE MIRAN TODOS LOS CAMPOS DEL DATO y ninguno de los otros: ni la `clave`
 * —que es distinta por construcción— ni `validada`, que dice quién revisó la
 * fila y no qué dice. Con `validada` adentro, volver a apretar el botón después
 * de validar una fuente agregaría su duplicado sin validar.
 */
function huellaDeFuente(fila: FilaDeFuenteDeAgua): string {
  return [
    fila.tipo,
    fila.rio_canal.trim(),
    fila.acciones.trim(),
    fila.litros_seg.trim(),
    fila.equivalencia_ls_accion.trim(),
    fila.estado_inscripcion,
  ].join("\u0000");
}

function huellaDeObra(fila: FilaDeObraDeAgua): string {
  return [fila.tipo, fila.capacidad_descripcion.trim()].join("\u0000");
}
